import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
} from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  HOTELLIST,
  ROOMADD,
  ROOMPRICING,
  ROOMPRICINGDETAIL,
  ROOMPRICINGUPDATE,
  ROOMTYPE,
  ROOMTYPELIST,
  ROOMUPDATE,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import { useTranslation } from "react-i18next";
import ConfirmationPop from "../../../../components/Modals/ConfirmationPop";

const pricingSchema = z.object({
  hotelId: z.string().nonempty("Hotel is required"),
  roomType: z.string().nonempty("Room type is required"),
  basePrice: z.coerce
    .number({
      invalid_type_error: "Base Price must be a number",
    })
    .min(0.01, "Base Price must be greater than 0"),

  finalPrice: z.coerce
    .number({
      invalid_type_error: "Final Price must be a number",
    })
    .min(0.01, "Final Price must be greater than 0"),
  discount: z.coerce
    .number({
      invalid_type_error: "Discount must be a number",
    })
    .min(0.01, "Discount must be greater than 0"),
  refundable: z.coerce.boolean().optional(),
  durationHours: z.coerce
    .number()
    .int("Duration Hours must be an integer")
    .min(1, "Duration Hours must be greater than 0"),
  pricingType: z.enum(["hour", "night", "day"], {
    required_error: "Pricing Type is required",
  }),
  pricingLabel: z.enum(["default", "weekend"], {
    required_error: "Pricing Label is required",
  })
});

const AddPricing = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const navigate = useNavigate();
  const [hotelAdminData, setHotelData] = useState([]);
  const [roomTypeData, setRoomTypeData] = useState([]);
  const [hotelDetailData, setHotelDetailData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roomModel, setRoomModel] = useState(false);


  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(pricingSchema),
    defaultValues: {
      hotelId: "",
      roomType: "",
      basePrice: 0,
      finalPrice: 0,
      discount: 0,
      refundable: false,
      durationHours: 0,
      pricingType: "hour",
      pricingLabel: "default"
    },
  });

  const selectedHotelId = watch("hotelId");

  const getAmenityData = catchAsync(async () => {
    const [hotelRes] = await Promise.all([
      HOTELLIST({ limit: 100, page: 1, status: "Active" }),
    ]);

    checkResponse({ res: hotelRes, setData: setHotelData });
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ROOMPRICINGDETAIL({ id: id });
    const success = checkResponse({
      res,
      setData: setHotelDetailData,
      setLoader,
    });


    if (success && res?.data?.data) {
      const data = res.data.data;
      console.log(data, "data====<>");
      reset({
        hotelId: data.hotelId?._id || "",
        roomType: data.roomType?._id || "",
        durationHours: data?.durationHours ?? 0,
        basePrice: data?.basePrice ?? 0,
        finalPrice: data?.finalPrice ?? 0,
        discount: data?.discount ?? 0,
        refundable: data?.refundable,
        pricingType: data?.pricingType ?? "",
        pricingLabel: data?.pricingLabel ?? ""
      });
    }
  }, setLoader);

  useEffect(() => {
    getAmenityData();
    if (id) {
      getData();
    }
  }, [id]);



  const submitHandler = catchAsync(async (data) => {
    setLoading(true);
    const body = {
      ...data,
      ...(id && { _id: id }),
    };

    const res = await (id ? ROOMPRICINGUPDATE(body) : ROOMPRICING(body));
    const success = checkResponse({ res, showSuccess: true });
    setLoading(false)
    if (success) navigate(`/pricing?page=${page}`);
  });

  const handleCancel = () => navigate(`/pricing?page=${page}`);
  console.log(hotelDetailData, "data values===<>");
  console.log(errors, "eroror");



  const fetchRoomType = async () => {
    const resp = await ROOMTYPELIST({ limit: 100, page: 1, hotelId: selectedHotelId });
    if (resp?.data?.data?.length <= 0) {
      setRoomModel(true)
    }
    checkResponse({ res: resp, setData: setRoomTypeData });
  }

  useEffect(() => {
    if (selectedHotelId) {
      fetchRoomType();
    }
  }, [selectedHotelId])

  return (
    <>
      <ConfirmationPop
        type={"addRoomType"}
        confirmation={roomModel}
        setConfirmation={() =>
          setRoomModel(false)
        }
        confirmHandler={() =>
          navigate("/roomtype/add")
        }
      />
      {loader && <Loader />}
      <section className="addRoom position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/pricing?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("pricing.editPricing") : t("pricing.addPricing")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div>
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="6" className="my-2">
                      <label>{t("pricing.columns.selectHotel")}</label>
                      <select className="form-control" {...register("hotelId")}>
                        <option value="">-- Select Hotel --</option>
                        {hotelAdminData?.hotelList?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.hotelName}
                          </option>
                        ))}
                      </select>
                      {errors?.hotelId && (
                        <p className="text-danger">{errors.hotelId.message}</p>
                      )}
                    </Col>



                    <Col lg="6" className="my-2">
                      <label> {t("pricing.columns.roomType")} <span style={{ color: "red", fontSize: 12, marginLeft: 5 }} >{!watch("hotelId") && t("manageRoom.columns.selectHotel")} </span></label>
                      <select key={JSON.stringify(roomTypeData)} className="form-control" {...register("roomType")}>
                        <option value="">-- Select Room Type --</option>
                        {roomTypeData?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.roomType.name}
                          </option>
                        ))}
                      </select>
                      {errors?.roomType && (
                        <p className="text-danger">{errors.roomType.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("pricing.columns.pricingType")}</label>

                      <Form.Select {...register("pricingType")}>
                        <option value="">Select Pricing Type</option>
                        <option value="hour">Hour</option>
                        <option value="night">Night</option>
                        <option value="day">Day</option>
                      </Form.Select>
                      {errors.pricingType && (
                        <p className="text-danger">{errors.pricingType.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>{t("pricing.columns.pricingLabel")}</label>

                      <Form.Select {...register("pricingLabel")}>
                        <option value="">Select Pricing Label</option>
                        <option value="default">Default</option>
                        <option value="weekend">Weekend</option>
                      </Form.Select>
                      {errors.pricingLabel && (
                        <p className="text-danger">{errors.pricingLabel.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>
                        {t("pricing.basePrice", "Base Price")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        step="0.01"
                        {...register("basePrice", { valueAsNumber: true })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.basePrice && (
                        <p className="text-danger">{errors.basePrice.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("pricing.columns.finalPrice", "Final Price")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        step="0.01"
                        {...register("finalPrice", { valueAsNumber: true })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.finalPrice && (
                        <p className="text-danger">{errors.finalPrice.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("pricing.columns.discount", "Discount")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("discount", {
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.discount && (
                        <p className="text-danger">{errors.discount.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("pricing.columns.durationHours", "Duration Hours")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("durationHours", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.durationHours && (
                        <p className="text-danger">{errors.durationHours.message}</p>
                      )}
                    </Col>
                    <Col
                      md={3}
                      className="checkboxlebels d-flex align-items-center gap-3 py-2 justify-content-start"
                    >

                      <input
                        type="checkbox"
                        className="chekboxCustom"
                        {...register(
                          `refundable`
                        )}
                      />
                      {t("pricing.columns.refundable", "Refundable")}
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="commonBtn">
                          {loading ? "Submitting..." : "Submit"}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddPricing;
